<template>
  <!-- Les écrans qui portent leur propre titre n'affichent pas de barre :
       un bandeau ne contenant qu'un logo occupait le haut de l'écran sans
       rien apporter. Ces écrans gèrent eux-mêmes la marge d'encoche. -->
  <header v-if="!sansBarre" class="oc-appbar">
    <div class="oc-appbar__slot">
      <button
        v-if="canGoBack"
        type="button"
        class="oc-appbar__back"
        aria-label="Retour"
        @click="goBack"
      >
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.1"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M14.5 5.5 8 12l6.5 6.5" />
        </svg>
      </button>
    </div>

    <img
      v-if="!canGoBack"
      class="oc-appbar__logo"
      src="/images/site-logo.png"
      alt="OuiCSport"
    >
    <h1 v-else class="oc-appbar__title">{{ title }}</h1>

    <div class="oc-appbar__slot">
      <slot name="action" />
    </div>
  </header>
</template>

<script>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * Barre d'application mobile.
 *
 * Sur les quatre écrans racines (ceux de la barre d'onglets) elle affiche le
 * logo. Ailleurs, elle affiche un bouton retour et un titre court : les titres
 * du routeur sont des balises SEO (« OuiCSport | Détails de l'Offre Sportive »)
 * qui débordent sur un écran de téléphone.
 *
 * Le logo est servi depuis le bundle (/images/site-logo.png, copié depuis
 * public/) et non depuis www.ouicsport.fr, pour que l'en-tête s'affiche même
 * sans réseau au lancement.
 */
export default {
  name: 'OcAppBar',

  setup() {
    const route = useRoute();
    const router = useRouter();

    // Écrans racines : atteints par la barre d'onglets, donc sans retour.
    const ROOT_ROUTES = ['HomePage', 'OffersList', 'Gallery', 'AccountPage', 'LoginPage'];

    const TITLES = {
      OfferDetailsPage: 'Détails',
      OfferDetailsPageV2: 'Réservation',
      ConfirmationPage: 'Confirmation',
      BookingSuccess: 'Réservation confirmée',
      MyOffers: 'Mes réservations',
      Profile: 'Mon profil',
      Contact: 'Contact',
      ForgotPasswordPage: 'Mot de passe oublié',
      ResetPasswordPage: 'Nouveau mot de passe',
      CGU: 'CGU / CGV',
      mentionslegales: 'Mentions légales',
      politiqueConfidentialite: 'Confidentialité',
    };

    // Écrans dont le balisage natif comporte déjà un titre pleine largeur.
    const SANS_BARRE = ['HomePage', 'OffersList', 'OfferDetailsPage', 'Gallery', 'LoginPage', 'AccountPage'];
    const sansBarre = computed(() => SANS_BARRE.includes(route.name));

    const canGoBack = computed(() => !ROOT_ROUTES.includes(route.name));
    const title = computed(() => TITLES[route.name] || 'OuiCSport');

    // window.history.state.back est renseigné par vue-router quand une page
    // précédente existe dans la pile. Sans elle (ouverture directe par un lien
    // profond ou une notification), revenir en arrière sortirait de l'app :
    // on retombe alors sur l'accueil.
    const goBack = () => {
      if (window.history.state && window.history.state.back) {
        router.back();
      } else {
        router.replace({ name: 'HomePage' });
      }
    };

    return { canGoBack, sansBarre, title, goBack };
  },
};
</script>
